import React, { useEffect, useRef } from "react";
import { Box } from "theme-ui";
import { loadGooglePlaces, parsePlaceAddress } from "./googlePlaces";

const fieldStyle = {
  width: "100%",
  height: 40,
  padding: "0 12px",
  border: "1px solid #d8dde3",
  borderRadius: 10,
  fontSize: 16,
  boxSizing: "border-box",
  background: "#FFFEFC",
  color: "#1f2933",
  outline: "none",
  transition: "border-color 150ms ease, box-shadow 150ms ease, background 150ms ease",
};

/**
 * Text input wired to Google Places address autocomplete (US addresses).
 */
export default function StreetAddressAutocomplete({
  label = "Street address",
  value,
  onChange,
  onPlaceSelected,
  id = "street-address",
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const onPlaceSelectedRef = useRef(onPlaceSelected);

  onChangeRef.current = onChange;
  onPlaceSelectedRef.current = onPlaceSelected;

  useEffect(() => {
    let cancelled = false;
    let loadedGoogle;

    loadGooglePlaces()
      .then((googleMaps) => {
        if (cancelled || !googleMaps || !inputRef.current) return;
        loadedGoogle = googleMaps;

        const autocomplete = new googleMaps.maps.places.Autocomplete(
          inputRef.current,
          {
            types: ["address"],
            componentRestrictions: { country: "us" },
            fields: ["address_components", "formatted_address"],
          }
        );

        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          const parsed = parsePlaceAddress(place);
          onChangeRef.current?.(parsed.line1);
          onPlaceSelectedRef.current?.(parsed);
        });

        autocompleteRef.current = autocomplete;
      })
      .catch((err) => {
        console.log("Google Places autocomplete unavailable", err);
      });

    return () => {
      cancelled = true;
      if (autocompleteRef.current && loadedGoogle) {
        loadedGoogle.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, []);

  return (
    <Box>
      <Box
        as="label"
        htmlFor={id}
        sx={{
          display: "block",
          mb: "6px",
          fontSize: 2,
          fontWeight: 500,
          color: "neutral.d1",
          letterSpacing: "0.01em",
        }}
      >
        {label}
      </Box>
      <input
        id={id}
        ref={inputRef}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        autoComplete="off"
        style={fieldStyle}
        onFocus={(e) => {
          e.target.style.borderColor = "#472BA4";
          e.target.style.background = "#ffffff";
          e.target.style.boxShadow = "0 0 0 3px rgba(71, 43, 164, 0.22)";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "#d8dde3";
          e.target.style.background = "#FFFEFC";
          e.target.style.boxShadow = "none";
        }}
      />
    </Box>
  );
}
